<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

type Props = {
  /**
   * If this component is a direct child of a flex-col container, it will cause the
   * parent to grow to accommodate the full height of this component's children, rather
   * than scrolling the content. Setting this prop to true should correct this
   */
  asFlexItem?: boolean,
  /**
   * If set to true, this will prevent the flex scroller content area from shrinking
   * smaller than it's grown to. A typical use case for this would be when you're
   * showing a list of something that the user can filter. If this prop is false,
   * as the user filters list items the container would shrink accordingly, causing
   * the footer to jump around
   */
  preventShrinking?: boolean;
};

const props = defineProps<Props>();

const contentContainer = ref<HTMLDivElement>(null);
const largestHeight = ref<number>();
const observer = ref<ResizeObserver>();

onMounted(() => {
  if (!contentContainer.value || !props.preventShrinking) return;

  largestHeight.value = contentContainer.value.getBoundingClientRect().height;

  observer.value = new ResizeObserver(entries => {
    const [entry] = entries;
    if (entry.contentRect.height > largestHeight.value) {
      largestHeight.value = entry.contentRect.height;
    }
  });

  observer.value.observe(contentContainer.value);
});

onBeforeUnmount(() => observer.value?.disconnect());
</script>
<template>
  <div class="w-full flex flex-col" :class="[props.asFlexItem ? 'grow' : 'h-full']">
    <div class="grow-0">
      <slot name="header" />
    </div>
    <div
      v-if="props.preventShrinking"
      ref="contentContainer"
      class="grow overflow-y-auto"
      :style="{height: largestHeight ? `${largestHeight}px` : undefined}"
    >
      <slot />
    </div>
    <div v-else class="grow overflow-y-auto">
      <slot />
    </div>
    <div class="grow-0">
      <slot name="footer" />
    </div>
  </div>
</template>
